import { FileCollection } from 'filelist-utils';
import { CURRENT_EXPORT_VERSION } from '../migration/MigrationManager';
import { FileExtensions } from '../utilities/files/constants';
import { getFileExtension } from '../utilities/files/getFileExtension';
import { generalFiltering } from '../utilities/generalFiltering';
import { readBruker } from './readBruker';
import { readJDF } from './readJDF/readJDF';
import { readJcamp } from './readJcamp';
import { readMolfile } from './readMolfile';
import { readNMReData } from './readNMReData';
import { readNMRium } from './readNMRium';
import { readSDF } from './readSDF';
import { readSMILES } from './readSMILES';
import { readVarian } from './readVarian/readVarian';
const extensions = Object.values(FileExtensions);
/**
 * it works over a FileCollection of several kind of data like jcamp, jeol (jdf), bruker folder, nmredata, and .nmrium files
 * if there is a .nmrium file in the fileCollection it will returns only the information of the first .nmrium it found.
 */
export async function read(input, options = {}) {
    const { logger } = options;
    const fileCollection = filterFileCollection(ensureFileCollection(input), options.sourceSelector?.files);
    const nmriumFile = retrieveNmriumFile(fileCollection);
    if (nmriumFile) {
        const toReturn = await readNMRium(nmriumFile, options);
        return {
            nmriumState: toReturn,
            containsNmrium: true,
        };
    }
    const data = { spectra: [], molecules: [] };
    const { sourceSelector: selector, converter, keepSource } = options;
    const { spectra: spectraBruker } = await readBruker(fileCollection, {
        converter: converter?.bruker,
        selector,
        keepSource,
        logger,
    });
    if (logger) {
        logger[spectraBruker.length > 0 ? 'info' : 'debug'](`Loaded ${spectraBruker.length} spectra from Bruker files`);
    }
    const { spectra: spectraVarian } = await readVarian(fileCollection, {
        logger,
        keepSource,
    });
    data.spectra.push(...spectraBruker, ...spectraVarian);
    for (const file of fileCollection) {
        if (!shouldProcessIt(file))
            continue;
        try {
            const { spectra = [], molecules = [] } = (await process(file, options)) || {};
            data.spectra.push(...spectra);
            data.molecules.push(...molecules);
        }
        catch (message) {
            if (logger)
                logger.error(message);
        }
    }
    return {
        nmriumState: generalFiltering({ version: CURRENT_EXPORT_VERSION, data }, options.sourceSelector?.general),
        containsNmrium: false,
    };
}
/**
 * returns a fileCollection from a FileCollectionItem
 */
function ensureFileCollection(input) {
    if (Array.isArray(input)) {
        throw new Error('For a set of fileCollectionItems pass a FileCollection instance');
    }
    return isFileCollection(input) ? input : new FileCollection([input]);
}
/**
 * check if input is a FileCollection instance
 */
function isFileCollection(input) {
    return typeof input === 'object' && 'files' in input;
}
/**
 * filter fileCollection if filesToSelect is defined
 */
function filterFileCollection(fileCollection, files) {
    return files
        ? fileCollection.filter((file) => files.includes(file.relativePath))
        : fileCollection;
}
/**
 * returns the first .nmrium file from a fileCollection
 */
export function retrieveNmriumFile(fileCollection) {
    return fileCollection.files.find((file) => {
        return getFileExtension(file.name).toLowerCase() === FileExtensions.NMRIUM;
    });
}
function shouldProcessIt(file) {
    return extensions.includes(getFileExtension(file.name).toLowerCase());
}
/**
 * process the fileCollectionItem by file extension, the extension is extracted from file name.
 */
export async function process(file, options) {
    const { sourceSelector: selector = {}, converter, keepSource, logger, } = options;
    const extension = getFileExtension(file.name);
    switch (extension) {
        case FileExtensions.MOL:
            logger?.debug(`Reading molfile file: ${file.name}`);
            return readMolfile(file);
        case FileExtensions.SDF:
            logger?.debug(`Reading sdf file: ${file.name}`);
            return readSDF(file, { logger });
        case FileExtensions.SMI:
        case FileExtensions.SMILES:
            return readSMILES(file, { logger });
        case FileExtensions.JDX:
        case FileExtensions.DX:
        case FileExtensions.JCAMP:
            logger?.debug(`Reading JCAMP-DX file: ${file.name}`);
            return readJcamp(file, {
                selector: getJcampSelector(selector),
                converter: converter?.jcamp,
                keepSource,
                logger,
            });
        case FileExtensions.JDF:
            logger?.debug(`Reading JDF file: ${file.name}`);
            return readJDF(file, { name: file.name, keepSource, logger });
        case FileExtensions.NMREDATA:
            return readNMReData(file, { selector, converter });
        default:
            return { spectra: [], molecules: [] };
    }
}
function getJcampSelector(selector = {}) {
    const { jcamp = {}, general = {} } = selector;
    const { dataSelection = 'both' } = general;
    return { ...jcamp, dataSelection };
}
//# sourceMappingURL=read.js.map